import React from 'react';
import { motion } from 'framer-motion';
import {
  FiGlobe, FiExternalLink, FiDatabase, FiDollarSign,
  FiFileText, FiShield, FiCreditCard, FiBookOpen,
  FiTruck, FiCheckCircle, FiAnchor, FiBox,
  FiClipboard, FiGrid, FiLayers
} from 'react-icons/fi';

const referenceLinks = [
  {
    title: 'ICEGATE',
    description: 'Indian Customs Electronic Gateway for filing Shipping Bills, Bills of Entry and e-Payment',
    url: 'https://www.icegate.gov.in',
    icon: FiAnchor,
    color: '#059669',
  },
  {
    title: 'Plant Quarantine',
    description: 'Directorate of Plant Protection, Quarantine & Storage for phytosanitary certificates',
    url: 'https://plantquarantineindia.nic.in',
    icon: FiShield,
    color: '#16a34a',
  },
  {
    title: 'XE Dollar Rates',
    description: 'Live USD/INR exchange rates and currency converter for invoice calculations',
    url: 'https://www.xe.com',
    icon: FiDollarSign,
    color: '#0d9488',
  },
  {
    title: 'DGFT',
    description: 'Directorate General of Foreign Trade for export-import policies, EPCG and advance authorizations',
    url: 'https://www.dgft.gov.in',
    icon: FiGlobe,
    color: '#047857',
  },
  {
    title: 'IEC / eBRC',
    description: 'Import Export Code registration and electronic Bank Realisation Certificate services',
    url: 'https://www.dgft.gov.in',
    icon: FiFileText,
    color: '#15803d',
  },
  {
    title: 'FEDAI',
    description: 'Foreign Exchange Dealers Association of India for forex rates and SWIFT information',
    url: 'https://www.fedai.org.in',
    icon: FiCreditCard,
    color: '#0e7490',
  },
  {
    title: 'DIPP',
    description: 'Department for Promotion of Industry and Internal Trade for FDI policies and industrial licensing',
    url: 'https://dpiit.gov.in',
    icon: FiDatabase,
    color: '#0369a1',
  },
  {
    title: 'MCA',
    description: 'Ministry of Corporate Affairs for company registration, filings and CIN lookup',
    url: 'https://www.mca.gov.in',
    icon: FiBookOpen,
    color: '#1d4ed8',
  },
  {
    title: 'ACU',
    description: 'Asian Clearing Union for trade settlement between member countries',
    url: 'https://www.asianclearingunion.org',
    icon: FiLayers,
    color: '#7c3aed',
  },
  {
    title: 'PAN Verification',
    description: 'Verify PAN card details through NSDL/UTIITSL for KYC and tax compliance',
    url: 'https://www.onlineservices.nsdl.com/paam/endUserRegisterContact.html',
    icon: FiCheckCircle,
    color: '#9333ea',
  },
  {
    title: 'AD Banks',
    description: 'Authorised Dealer banks information for foreign exchange transactions and trade finance',
    url: 'https://www.rbi.org.in',
    icon: FiClipboard,
    color: '#c026d3',
  },
  {
    title: 'ECGC',
    description: 'Export Credit Guarantee Corporation for export credit insurance and risk management',
    url: 'https://www.ecgc.in',
    icon: FiShield,
    color: '#db2777',
  },
  {
    title: 'Swift Codes',
    description: 'Lookup SWIFT/BIC codes for international bank wire transfers',
    url: 'https://www.swift.com',
    icon: FiGrid,
    color: '#e11d48',
  },
  {
    title: 'HS Codes',
    description: 'Harmonized System codes lookup via CBIC for customs tariff classification',
    url: 'https://www.cbic.gov.in',
    icon: FiBox,
    color: '#dc2626',
  },
  {
    title: 'CBEC / CBIC',
    description: 'Central Board of Indirect Taxes and Customs for duty drawback, GST and customs notifications',
    url: 'https://www.cbic.gov.in',
    icon: FiTruck,
    color: '#ea580c',
  },
  {
    title: 'Commercial Taxes WB',
    description: 'West Bengal Commercial Taxes Directorate for state GST, VAT and trade facilitation',
    url: 'https://wbcomtax.gov.in',
    icon: FiFileText,
    color: '#d97706',
  },
];

// ── Styles ──────────────────────────────────────────────────────────────────────

const styles = {
  container: {
    fontFamily: 'Inter, sans-serif',
  },
  header: {
    marginBottom: 28,
  },
  title: {
    fontFamily: 'Poppins, sans-serif',
    fontSize: 28,
    fontWeight: 800,
    color: '#064e3b',
    margin: 0,
  },
  subtitle: {
    fontSize: 14,
    color: '#6b7280',
    marginTop: 4,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: 18,
  },
  card: {
    background: 'rgba(255, 255, 255, 0.65)',
    backdropFilter: 'blur(20px)',
    borderRadius: 18,
    border: '1px solid rgba(255, 255, 255, 0.5)',
    boxShadow: '0 4px 24px rgba(6, 78, 59, 0.06)',
    padding: '22px 20px',
    cursor: 'pointer',
    textDecoration: 'none',
    display: 'block',
    transition: 'all 0.25s ease',
    position: 'relative',
    overflow: 'hidden',
  },
  cardHoverOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.03) 0%, rgba(6, 78, 59, 0.04) 100%)',
    opacity: 0,
    transition: 'opacity 0.25s ease',
    borderRadius: 18,
    pointerEvents: 'none',
  },
  iconWrapper: {
    width: 44,
    height: 44,
    borderRadius: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 14,
    transition: 'transform 0.25s ease',
  },
  cardTitle: {
    fontFamily: 'Poppins, sans-serif',
    fontSize: 15,
    fontWeight: 700,
    color: '#064e3b',
    marginBottom: 6,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  cardDesc: {
    fontSize: 13,
    color: '#6b7280',
    lineHeight: 1.5,
    margin: 0,
  },
  externalIcon: {
    position: 'absolute',
    top: 16,
    right: 16,
    color: '#9ca3af',
    opacity: 0.5,
    transition: 'all 0.25s ease',
  },
};

// ── Animation variants ──────────────────────────────────────────────────────────

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
      delayChildren: 0.15,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 20, scale: 0.96 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: 'spring',
      stiffness: 260,
      damping: 20,
    },
  },
};

// ── Component ───────────────────────────────────────────────────────────────────

export default function ReferenceLinks() {
  return (
    <div style={styles.container}>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        style={styles.header}
      >
        <h1 style={styles.title}>Reference Links</h1>
        <p style={styles.subtitle}>
          Quick access to Indian export regulatory portals and trade resources
        </p>
      </motion.div>

      {/* Card Grid */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        style={styles.grid}
      >
        {referenceLinks.map((link, idx) => {
          const IconComponent = link.icon;
          return (
            <motion.a
              key={idx}
              variants={cardVariants}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              style={styles.card}
              whileHover={{
                y: -4,
                boxShadow: '0 12px 40px rgba(6, 78, 59, 0.12)',
                borderColor: 'rgba(16, 185, 129, 0.3)',
              }}
              whileTap={{ scale: 0.98 }}
              onMouseEnter={(e) => {
                const overlay = e.currentTarget.querySelector('[data-overlay]');
                if (overlay) overlay.style.opacity = '1';
                const extIcon = e.currentTarget.querySelector('[data-exticon]');
                if (extIcon) {
                  extIcon.style.opacity = '1';
                  extIcon.style.color = link.color;
                }
                const iconWrap = e.currentTarget.querySelector('[data-icon]');
                if (iconWrap) iconWrap.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                const overlay = e.currentTarget.querySelector('[data-overlay]');
                if (overlay) overlay.style.opacity = '0';
                const extIcon = e.currentTarget.querySelector('[data-exticon]');
                if (extIcon) {
                  extIcon.style.opacity = '0.5';
                  extIcon.style.color = '#9ca3af';
                }
                const iconWrap = e.currentTarget.querySelector('[data-icon]');
                if (iconWrap) iconWrap.style.transform = 'scale(1)';
              }}
            >
              {/* Hover overlay */}
              <div data-overlay style={styles.cardHoverOverlay} />

              {/* External link icon */}
              <div data-exticon style={styles.externalIcon}>
                <FiExternalLink size={14} />
              </div>

              {/* Icon */}
              <div
                data-icon
                style={{
                  ...styles.iconWrapper,
                  background: `${link.color}15`,
                }}
              >
                <IconComponent size={22} color={link.color} />
              </div>

              {/* Content */}
              <div style={styles.cardTitle}>{link.title}</div>
              <p style={styles.cardDesc}>{link.description}</p>
            </motion.a>
          );
        })}
      </motion.div>

      {/* Footer note */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        style={{
          textAlign: 'center',
          marginTop: 36,
          paddingTop: 20,
          borderTop: '1px solid rgba(16, 185, 129, 0.1)',
        }}
      >
        <p style={{ fontSize: 12, color: '#9ca3af' }}>
          Links open in a new browser tab. External websites are maintained by their respective government bodies.
        </p>
      </motion.div>
    </div>
  );
}
